import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';

import DoneIcon from '@mui/icons-material/Done';

import CommonSelect, { Option } from '../../common/commonSelect/CommonSelect';
import '../ReadOnlyTable.css';
import {
  OptionsBase,
  OptionsSelectList,
  attrTypeLabelMap,
  useDivisionOptions,
  useTeamOptions,
  useReportTypeOptions,
  useProjectOptions,
  useGradeOptions,
  usePowerTrainOptions,
  useShimukeOptions,
  useLotOptions,
  useFreeOptions,
} from '../../../state/master';
import { filterOptionsByOrg } from '../../../lib/filters';
import { apiClient } from '../../../lib/apiClient';
import {
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';

export type OptionsAddDialogResult = {
  useExistingOrg: boolean;
  divisionId: number;
  teamId: number;
  copySameEnlightConnection: boolean;
  value: string;
  color: string;
};

type OrgSelection = 'use' | 'no' | '';

type Props = {
  open: boolean;
  fullScreen?: boolean;
  dialogTitle?: string;
  valueLabel?: string;
  showOrgSelection?: boolean;
  showColorSetting?: boolean;
  initialDivisionId?: number;
  initialTeamId?: number;
  orgSelection?: OrgSelection;
  divisionId?: number;
  teamId?: number;
  onOrgSelectionChange?: (next: OrgSelection) => void;
  onDivisionIdChange?: (next: number) => void;
  onTeamIdChange?: (next: number) => void;
  onClose: () => void;
  onOk: (result: OptionsAddDialogResult) => void;
  canEdit?: boolean;
};

function createSettingData(
  setting_id: number,
  meta: OptionsBase,
  name: string,
  type: string,
  required: boolean,
  status: any,
  multiple_select?: boolean,
  datas?: OptionsSelectList[]
) {
  return { setting_id, meta, name, type, required, status, multiple_select, datas };
}

function mapAttrTypeToLabel(attrType?: number | string, fallback?: string): string {
  const n = Number(attrType);
  if (!Number.isFinite(n)) return fallback ?? '';
  return attrTypeLabelMap[n] ?? (fallback ?? '');
}

const OptionsAddDialog: React.FC<Props> = (props) => {
  const {
    open,
    fullScreen,
    dialogTitle = '項目設定',
    valueLabel = '値',
    showOrgSelection = false,
    showColorSetting = false,
    initialDivisionId,
    initialTeamId,
    orgSelection: orgSelectionProp,
    divisionId: divisionIdProp,
    teamId: teamIdProp,
    onOrgSelectionChange,
    onDivisionIdChange,
    onTeamIdChange,
    onClose,
    onOk,
    canEdit = true,
  } = props;

  const effectiveValueLabel = valueLabel.startsWith('新規作成する') ? valueLabel : `新規作成する${valueLabel}`;

  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const [orgSelectionInternal, setOrgSelectionInternal] = React.useState<OrgSelection>('');
  const [divisionIdInternal, setDivisionIdInternal] = React.useState<number>(0);
  const [teamIdInternal, setTeamIdInternal] = React.useState<number>(0);

  const orgSelection = orgSelectionProp ?? orgSelectionInternal;
  const divisionId = divisionIdProp ?? divisionIdInternal;
  const teamId = teamIdProp ?? teamIdInternal;

  const { freeOptions: freeOptionsFetched } = useFreeOptions({ department_id: divisionId, team_id: teamId });

  const setOrgSelectionValue = (next: OrgSelection) => {
    if (onOrgSelectionChange) return onOrgSelectionChange(next);
    setOrgSelectionInternal(next);
  };
  const setDivisionIdValue = (next: number) => {
    if (onDivisionIdChange) return onDivisionIdChange(next);
    setDivisionIdInternal(next);
  };
  const setTeamIdValue = (next: number) => {
    if (onTeamIdChange) return onTeamIdChange(next);
    setTeamIdInternal(next);
  };

  const useExistingOrg = orgSelection === 'use';
  const isSameDivisionReference = Number(divisionId) > 0 && Number(divisionId) === Number(initialDivisionId ?? 0);
  const [inputValue, setInputValue] = React.useState<string>('');
  const [inputColor, setInputColor] = React.useState<string>('');
  const [copySameEnlightConnection, setCopySameEnlightConnection] = React.useState<boolean>(false);
  const [linkedEnlightTeamName, setLinkedEnlightTeamName] = React.useState<string>('');
  const [loadingLinkedEnlight, setLoadingLinkedEnlight] = React.useState<boolean>(false);

  const { reportTypeOptionsRaw } = useReportTypeOptions({ department_id: divisionId, team_id: teamId || undefined });
  const { projectOptionsRaw } = useProjectOptions({ department_id: divisionId, team_id: teamId || undefined });
  const { powerTrainOptionsRaw } = usePowerTrainOptions({ department_id: divisionId, team_id: teamId || undefined });
  const { gradeOptionsRaw } = useGradeOptions({ department_id: divisionId, team_id: teamId || undefined });
  const { shimukeOptionsRaw } = useShimukeOptions({ department_id: divisionId, team_id: teamId || undefined });
  const { lotOptionsRaw } = useLotOptions({ department_id: divisionId, team_id: teamId || undefined });

  const prevOpenRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    if (!open) return;
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;
    setInputValue('');
    setInputColor(showColorSetting ? '#000000' : '');
    if (orgSelectionProp == null) setOrgSelectionInternal('');
    if (divisionIdProp == null) setDivisionIdInternal(Number(initialDivisionId ?? 0));
    if (teamIdProp == null) setTeamIdInternal(Number(initialTeamId ?? 0));
    setCopySameEnlightConnection(false);
    setLinkedEnlightTeamName('');
    setLoadingLinkedEnlight(false);
  }, [open, initialDivisionId, initialTeamId, showColorSetting, orgSelectionProp, divisionIdProp, teamIdProp]);

  React.useEffect(() => {
    prevOpenRef.current = open;
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    if (!showOrgSelection || !useExistingOrg || !isSameDivisionReference || !divisionId || !teamId) {
      setLinkedEnlightTeamName('');
      setLoadingLinkedEnlight(false);
      setCopySameEnlightConnection(false);
      return;
    }

    let cancelled = false;
    const normalizeEnlightTeam = (v: any): string => {
      const s = String(v ?? '').trim();
      if (!s) return '';
      const lc = s.toLowerCase();
      if (lc === 'none' || lc === 'null') return '';
      return s;
    };
    const extractEnlightTeamFromDocset = (data: any): string => {
      const arr = Array.isArray(data)
        ? data
        : Array.isArray((data as any)?.list)
          ? (data as any).list
          : Array.isArray((data as any)?.data)
            ? (data as any).data
            : data
              ? [data]
              : [];
      if (!Array.isArray(arr) || arr.length === 0) {
        return normalizeEnlightTeam((data as any)?.enlight_team ?? (data as any)?.enlightTeam);
      }
      const hit = arr.find((x: any) => normalizeEnlightTeam((x as any)?.enlight_team ?? (x as any)?.enlightTeam));
      if (!hit) return '';
      return normalizeEnlightTeam((hit as any)?.enlight_team ?? (hit as any)?.enlightTeam);
    };

    const run = async () => {
      setLoadingLinkedEnlight(true);
      try {
        const res = await apiClient.get('/get_team_docset_list', {
          params: {
            department_id: Number(divisionId) || 0,
            team_id: Number(teamId) || 0,
          },
        });
        if (cancelled) return;
        const name = extractEnlightTeamFromDocset(res?.data);
        setLinkedEnlightTeamName(name);
        setCopySameEnlightConnection(Boolean(name));
      } catch (e) {
        if (cancelled) return;
        console.warn('get_team_docset_list failed in OptionsAddDialog', e);
        setLinkedEnlightTeamName('');
        setCopySameEnlightConnection(false);
      } finally {
        if (!cancelled) setLoadingLinkedEnlight(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, showOrgSelection, useExistingOrg, isSameDivisionReference, divisionId, teamId]);

  const normalizeMultiple = (v: any): boolean => Number(v) === 1;
  const toOrgId = (v: any): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const pickPreferredMeta = (candidates?: OptionsBase[]): OptionsBase | undefined => {
    const list = (Array.isArray(candidates) ? candidates : []).filter(Boolean) as OptionsBase[];
    if (list.length === 0) return undefined;

    const depId = toOrgId(divisionId);
    const selectedTeamId = toOrgId(teamId);
    const score = (item: any): number => {
      const did = toOrgId((item as any)?.department_id);
      const tid = toOrgId((item as any)?.team_id);
      let s = 0;
      if (depId > 0 && selectedTeamId > 0) {
        if (did === depId && tid === selectedTeamId) s = 5000;
        else if (did === depId && tid === 0) s = 4000;
        else if (did === 0 && tid === 0) s = 3000;
        else if (did === depId) s = 2000;
        else if (did === 0) s = 1000;
      } else if (depId > 0) {
        if (did === depId && tid === 0) s = 4500;
        else if (did === depId) s = 3500;
        else if (did === 0 && tid === 0) s = 2500;
        else if (did === 0) s = 1500;
      } else {
        if (did === 0 && tid === 0) s = 2000;
        else if (did === 0) s = 1000;
        else s = 500;
      }
      if (String((item as any)?.status ?? '').toUpperCase() === 'Y') s += 100;
      s += Math.min(99, Number((item as any)?.options?.length ?? 0));
      return s;
    };

    const picked = [...list].sort((a, b) => score(b) - score(a))[0];
    const name = String((picked as any)?.attr_name ?? picked?.label ?? '').trim();
    const optionsLen = Array.isArray((picked as any)?.options) ? (picked as any).options.length : 0;
    const typeNum = Number((picked as any)?.attr_type);
    const isSelectLike = [11, 12, 13].includes(typeNum);
    const isCommonFixedName = [CLASSIFICATION_MARKET_TARGET_ATTR_NAME, FIXED_ATTR_NAME_PROJECT, FIXED_ATTR_NAME_POWERTRAIN, 'グレード', FIXED_ATTR_NAME_SHIMUKE, FIXED_ATTR_NAME_LOT].includes(name);
    if (isCommonFixedName && optionsLen > 0 && !isSelectLike) {
      return { ...(picked as any), attr_type: 11 } as OptionsBase;
    }
    return picked;
  };

  const commonRows = React.useMemo(() => {
    const reportTypeMeta = pickPreferredMeta(reportTypeOptionsRaw);
    const projectMeta = pickPreferredMeta(projectOptionsRaw);
    const powerTrainMeta = pickPreferredMeta(powerTrainOptionsRaw);
    const gradeMeta = pickPreferredMeta(gradeOptionsRaw);
    const shimukeMeta = pickPreferredMeta(shimukeOptionsRaw);
    const lotMeta = pickPreferredMeta(lotOptionsRaw);

    const rows = [
      reportTypeMeta
        ? createSettingData(
          reportTypeMeta.attr_id,
          reportTypeMeta,
          reportTypeMeta.label,
          reportTypeMeta.type ?? '',
          reportTypeMeta.required,
          reportTypeMeta.status,
          normalizeMultiple(reportTypeMeta.multiple_select),
          reportTypeMeta.options
        )
        : null,
      projectMeta
        ? createSettingData(
          projectMeta.attr_id,
          projectMeta,
          projectMeta.label,
          projectMeta.type ?? '',
          projectMeta.required,
          projectMeta.status,
          normalizeMultiple(projectMeta.multiple_select),
          projectMeta.options
        )
        : null,
      powerTrainMeta
        ? createSettingData(
          powerTrainMeta.attr_id,
          powerTrainMeta,
          powerTrainMeta.label,
          powerTrainMeta.type ?? '',
          powerTrainMeta.required,
          powerTrainMeta.status,
          normalizeMultiple(powerTrainMeta.multiple_select),
          powerTrainMeta.options
        )
        : null,
      gradeMeta
        ? createSettingData(
          gradeMeta.attr_id,
          gradeMeta,
          gradeMeta.label,
          gradeMeta.type ?? '',
          gradeMeta.required,
          gradeMeta.status,
          normalizeMultiple(gradeMeta.multiple_select),
          gradeMeta.options
        )
        : null,
      shimukeMeta
        ? createSettingData(
          shimukeMeta.attr_id,
          shimukeMeta,
          shimukeMeta.label,
          shimukeMeta.type ?? '',
          shimukeMeta.required,
          shimukeMeta.status,
          normalizeMultiple(shimukeMeta.multiple_select),
          shimukeMeta.options
        )
        : null,
      lotMeta
        ? createSettingData(
          lotMeta.attr_id,
          lotMeta,
          lotMeta.label,
          lotMeta.type ?? '',
          lotMeta.required,
          lotMeta.status,
          normalizeMultiple(lotMeta.multiple_select),
          lotMeta.options
        )
        : null,
    ].filter(Boolean) as any[];

    return rows;
  }, [
    reportTypeOptionsRaw,
    projectOptionsRaw,
    powerTrainOptionsRaw,
    gradeOptionsRaw,
    shimukeOptionsRaw,
    lotOptionsRaw,
    divisionId,
    teamId,
  ]);

  const freeRows = React.useMemo(() => {
    const freeOptions = (freeOptionsFetched ?? []).filter(
      (state: any) => Number(state?.department_id) === Number(divisionId) && Number(state?.team_id) === Number(teamId)
    );
    return freeOptions.map((freeRow: any) =>
      createSettingData(
        freeRow.attr_id,
        freeRow,
        freeRow.label,
        freeRow.type ?? '',
        freeRow.required,
        freeRow.status,
        normalizeMultiple(freeRow.multiple_select),
        freeRow.options
      )
    );
  }, [freeOptionsFetched, divisionId, teamId]);

  const filteredTeamOptions = React.useMemo(() => {
    return (teamOptions ?? []).filter((state: any) => Number(state?.department_id) === Number(divisionId));
  }, [teamOptions, divisionId]);

  const divisionSelectOptions: Option[] = React.useMemo(() => {
    return (divisionOptions ?? []).map((row: any) => ({
      value: String(row?.department_id ?? row?.value ?? ''),
      label: String(row?.label ?? ''),
    }));
  }, [divisionOptions]);

  const teamSelectOptions: Option[] = React.useMemo(() => {
    return (filteredTeamOptions ?? []).map((row: any) => ({
      value: String(row?.team_id ?? row?.value ?? ''),
      label: String(row?.label ?? ''),
    }));
  }, [filteredTeamOptions]);

  const selectedDivision: Option | null =
    divisionId ? (divisionSelectOptions.find((o) => Number(o.value) === Number(divisionId)) ?? null) : null;
  const selectedTeam: Option | null =
    teamId ? (teamSelectOptions.find((o) => Number(o.value) === Number(teamId)) ?? null) : null;

  const hasValidDivisionSelection = Boolean(selectedDivision && String(selectedDivision.value ?? '').trim() !== '');
  const hasValidTeamSelection = Boolean(selectedTeam && String(selectedTeam.value ?? '').trim() !== '');

  const handleDivisionChange = (opt: Option | null) => {
    const nextDivision = Number(opt?.value ?? 0);
    setDivisionIdValue(nextDivision);

    const nextTeamOptions = (teamOptions ?? []).filter((state: any) => Number(state?.department_id) === Number(nextDivision));
    if (nextTeamOptions.length > 0) {
      const firstTeamId = Number((nextTeamOptions[0] as any)?.team_id ?? 0);
      setTeamIdValue(firstTeamId);
    } else {
      setTeamIdValue(0);
    }
  };

  const handleTeamChange = (opt: Option | null) => {
    setTeamIdValue(Number(opt?.value ?? 0));
  };

  const okDisabled =
    inputValue.trim() === '' ||
    (showOrgSelection && (
      orgSelection === '' ||
      (useExistingOrg && (
        !divisionId ||
        !teamId ||
        !hasValidDivisionSelection ||
        !hasValidTeamSelection
      ))
    ));

  const handleDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullScreen={fullScreen} fullWidth maxWidth="sm">
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
          <TextField
            label={effectiveValueLabel}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            required
            fullWidth
            size="small"
            disabled={!canEdit}
          />
          {showColorSetting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ minWidth: 32 }}>色</Typography>
              <input
                type="color"
                className="color-setting"
                style={{ width: 36, height: 36, padding: 0, border: 'none', background: 'transparent', borderRadius: '50%', cursor: 'pointer' }}
                value={inputColor || '#000000'}
                onChange={(event) => setInputColor(event.target.value)}
                disabled={!canEdit}
              />
            </Box>
          )}
          {showOrgSelection && (
            <>
              <Box>
  <Typography>
    追加するチームのマスタ設定に、他の課・チームの内容を参照しますか、
  </Typography>
  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
    （※コピーとなるので、参照元のマスタ設定が更新されても、本チームの内容には反映されません）
  </Typography>
</Box>

              <FormControl>
                <RadioGroup
                  row
                  value={orgSelection}
                  onChange={(e) => setOrgSelectionValue(e.target.value as any)}
                >
                  <FormControlLabel value="no" control={<Radio />} label="参照しない" disabled={!canEdit} />
                  <FormControlLabel value="use" control={<Radio />} label="参照する" disabled={!canEdit} />
                </RadioGroup>
              </FormControl>

              {useExistingOrg && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
                  <Grid container spacing={2}>
                    <Grid size={6} display="flex" justifyContent="center" alignItems="center">
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 0.5, fontSize: '0.75rem' }}>参照する課</FormLabel>
                        <CommonSelect
                          id="team-add-division"
                          options={divisionSelectOptions}
                          value={selectedDivision}
                          onChange={handleDivisionChange}
                          placeholder="課を入力して絞り込み..."
                          isClearable={false}
                          isSearchable={true}
                          allowCreate={false}
                          isDisabled={!canEdit}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={6} display="flex" justifyContent="center" alignItems="center">
                      <FormControl fullWidth>
                        <FormLabel sx={{ mb: 0.5, fontSize: '0.75rem' }}>チーム</FormLabel>
                        <CommonSelect
                          id="team-add-team"
                          options={teamSelectOptions}
                          value={selectedTeam}
                          onChange={handleTeamChange}
                          placeholder="チームを入力して絞り込み..."
                          isClearable={false}
                          isSearchable={true}
                          allowCreate={false}
                          isDisabled={!canEdit}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  {isSameDivisionReference && (
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid size={3}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>EnlightID連携</Typography>
                        </Grid>
                        <Grid size={5}>
                          {loadingLinkedEnlight ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CircularProgress size={14} />
                              <Typography variant="caption" color="text.secondary">読込中...</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color={linkedEnlightTeamName ? 'text.primary' : 'text.secondary'}>
                              {linkedEnlightTeamName || '未設定'}
                            </Typography>
                          )}
                        </Grid>
                        <Grid size={4}>
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={copySameEnlightConnection}
                                onChange={(e) => setCopySameEnlightConnection(Boolean(e.target.checked))}
                                disabled={!canEdit || loadingLinkedEnlight || !linkedEnlightTeamName}
                              />
                            }
                            label={<Typography variant="caption">同じEnlightID連携を設定する</Typography>}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  <Divider component="hr" sx={{ my: 0 }} />

                  <Box
                    id="viewBaseSetting"
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      pr: 0.5,
                      pb: 2,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>・共通項目</Typography>
                      </Grid>
                      <Grid size={12}>
                        <TableContainer component={Paper} className={canEdit ? undefined : 'read-only-table'}>
                          <Table
                            stickyHeader
                            sx={{ minWidth: 650, fontSize: '0.85rem', '& .MuiTableCell-root': { fontSize: '0.85rem' } }}
                            aria-label="common items table"
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>項目</TableCell>
                                <TableCell>入力方法</TableCell>
                                <TableCell sx={{ width: 96 }}>項目表示</TableCell>
                                <TableCell sx={{ width: 96 }}>複数選択</TableCell>
                                <TableCell>設定値</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {commonRows.map((row: any) => (
                                <TableRow key={`common-${row.setting_id}`}>
                                  <TableCell component="td" scope="row">
                                    <strong>{row.name}</strong>
                                  </TableCell>
                                  <TableCell component="td" scope="row">
                                    {mapAttrTypeToLabel(row.meta?.attr_type, row.type)}
                                  </TableCell>
                                  <TableCell component="td" scope="row">
                                    {String(row.status ?? '').toUpperCase() === 'Y' && (
                                      <DoneIcon color="primary" />
                                    )}
                                  </TableCell>
                                  <TableCell component="td" scope="row">
                                    {row.multiple_select && (
                                      <DoneIcon color="primary" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 8 }}>
                                      {Array.isArray(row.datas) && filterOptionsByOrg(row.datas as OptionsSelectList[], divisionId, teamId)
                                        .filter((line: OptionsSelectList) => String((line as any).status ?? 'Y').toUpperCase() !== 'N')
                                        .map((line: OptionsSelectList) => {
                                          const color = (line as any).color;
                                          if (color) {
                                            return (
                                              <p style={{ margin: 0 }} key={String(line.value)}>
                                                {line.value}
                                                <input type="color" value={color} disabled style={{ marginLeft: 6, width: 18, height: 18, padding: 0, border: 'none', background: 'transparent' }} />
                                              </p>
                                            );
                                          }
                                          return (
                                            <p style={{ margin: 0 }} key={String(line.value)}>
                                              {line.value}
                                            </p>
                                          );
                                        })}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {commonRows.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>データはありません</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>

                      <Grid size={12}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>・フリー項目</Typography>
                      </Grid>
                      <Grid size={12}>
                        <TableContainer component={Paper}>
                          <Table
                            stickyHeader
                            sx={{ minWidth: 650, fontSize: '0.85rem', '& .MuiTableCell-root': { fontSize: '0.85rem' } }}
                            aria-label="free items table"
                          >
                            <TableHead>
                              <TableRow>
                                <TableCell>項目</TableCell>
                                <TableCell>入力方法</TableCell>
                                <TableCell sx={{ width: 96 }}>必須</TableCell>
                                <TableCell sx={{ width: 96 }}>複数選択</TableCell>
                                <TableCell>設定値</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {freeRows.map((row: any) => (
                                <TableRow key={`free-${row.setting_id}`}>
                                  <TableCell component="td" scope="row">
                                    <strong>{row.name}</strong>
                                  </TableCell>
                                  <TableCell component="td" scope="row">
                                    {mapAttrTypeToLabel(row.meta?.attr_type, row.type)}
                                  </TableCell>
                                  <TableCell component="td" scope="row">
                                    {Boolean(row.required) && <DoneIcon color="primary" />}
                                  </TableCell>
                                  <TableCell component="td" scope="row">
                                    {row.multiple_select && <DoneIcon color="primary" />}
                                  </TableCell>
                                  <TableCell>
                                    <div style={{ maxHeight: 240, overflowY: 'auto', paddingRight: 8 }}>
                                      {Array.isArray(row.datas) && filterOptionsByOrg(row.datas as OptionsSelectList[], divisionId, teamId)
                                        .filter((line: OptionsSelectList) => String((line as any).status ?? 'Y').toUpperCase() !== 'N')
                                        .map((line: OptionsSelectList) => {
                                          const color = (line as any).color;
                                          const label = String((line as any).label ?? (line as any).value ?? '');
                                          if (color) {
                                            return (
                                              <p style={{ margin: 0 }} key={String(line.value)}>
                                                {label}
                                                <input type="color" value={color} disabled style={{ marginLeft: 6, width: 18, height: 18, padding: 0, border: 'none', background: 'transparent' }} />
                                              </p>
                                            );
                                          }
                                          return (
                                            <p style={{ margin: 0 }} key={String(line.value)}>
                                              {label}
                                            </p>
                                          );
                                        })}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {freeRows.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>データはありません</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disabled={!canEdit || okDisabled}
          onClick={() =>
            onOk({
              useExistingOrg,
              divisionId,
              teamId,
              copySameEnlightConnection: Boolean(copySameEnlightConnection && isSameDivisionReference && linkedEnlightTeamName),
              value: inputValue.trim(),
              color: inputColor,
            })
          }
        >
          OK
        </Button>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptionsAddDialog;
